import type { ComponentProps, FC, Ref } from 'react'

import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { t } from 'i18next'

import { Icon } from '@/components/icon'
import { cn } from '@/infra'

export const buttonVariants = cva(
  'font-nunito cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border border-sky-700 text-sky-700 bg-foreground hover:bg-accent hover:text-sky-900',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-6 rounded-md px-3 text-xs',
        lg: 'h-12 rounded-md px-8',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps extends ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
  ref?: Ref<HTMLButtonElement>
  isLoading?: boolean
  loadingText?: string
  showLoadingText?: boolean
}

const Button: FC<ButtonProps> = ({
  isLoading = false,
  loadingText = t('common:carregando'),
  className,
  variant,
  size,
  asChild = false,
  children,
  showLoadingText = true,
  ...props
}) => {
  const Comp = asChild ? Slot : 'button'
  return (
    <Comp
      className={cn(buttonVariants({ variant, size }), className, { relative: isLoading })}
      type="button"
      disabled={isLoading}
      {...props}
    >
      {isLoading ? (
        <div className="absolute inset-0 flex items-center justify-center gap-2">
          <Icon name="loader" className="animate-spin" />
          {showLoadingText && <span>{loadingText}</span>}
        </div>
      ) : (
        children
      )}
    </Comp>
  )
}

Button.displayName = 'Button'

export { Button }
